import cx from "classnames";
import React, { type Ref, useCallback, useState } from "react";

import { safeStringify } from "@moment/api-collab";
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, CopyIcon } from "@moment/icons";

import { writeClipboardText } from "~/components/common/clipboard-util";

import { type ExpandedFieldStateManager } from "../traverseValue/ExpandedFieldStateManager";
import { type ValuePath } from "../traverseValue/ValuePath";
import { classDoNotCollapseQueryBuilderOnClick } from "./hasClassDoNotCollapseQueryBuilderOnClick";
import {
	classDoNotExpandobjectInspectorOnClick,
	hasClassDoNotExpandObjectInspectorOnClick,
} from "./hasClassDoNotExpandOnClick";
import { type InspectorKeyNavigator } from "./useInspectorKeyNavigation";

export interface InspectorRowProps {
	children: React.ReactNode;
	navigator: InspectorKeyNavigator;
	prefix: ValuePath;
	fieldExpander: ExpandedFieldStateManager;
	showExpandButton?: boolean;
	ref: Ref<HTMLButtonElement>;
}

/**
 * Displays the expand/collapse button and indentation that precedes a value in the object
 * inspector. For example, an object will be displayed as follows, and this component is responsible
 * for rendering the caret `>` and the space before the actual value.
 *
 *     v myCell = {
 *         a: "b",
 *     >   c: { d: "e" },
 *       }
 */
const _InspectorRow = (
	{ children, navigator, prefix, fieldExpander, ...rest }: InspectorRowProps,
	buttonRef: React.ForwardedRef<HTMLButtonElement>
) => {
	const indentLevel = prefix.length - 1;
	const spacing = "\u00A0\u00A0".repeat(indentLevel); // Unicode version of `&nbsp;`.
	const showExpandButton = rest.showExpandButton === undefined ? true : rest.showExpandButton;

	const expanded = fieldExpander.expanded(prefix);

	// eslint-disable-next-line react-hooks/rules-of-hooks -- TODO
	const keydown = React.useCallback(
		(e: React.KeyboardEvent<HTMLButtonElement>) => {
			// Use keydown because it keypress does not register for arrow keys.
			switch (e.key) {
				case "ArrowRight":
					fieldExpander.expand(prefix);
					e.preventDefault();
					break;
				case "ArrowLeft":
					if (fieldExpander.expanded(prefix)) {
						fieldExpander.collapse(prefix);
					} else {
						navigator.focusButtonOfParent();
					}

					e.preventDefault();
					break;

				case "ArrowUp":
					navigator.focusPreviousButton();
					e.preventDefault();
					break;
				case "ArrowDown":
					navigator.focusNextButton();
					e.preventDefault();
					break;
			}
		},
		[fieldExpander, navigator, prefix]
	);

	const expandButton = (
		<button
			className="hover:bg-button-normal-tertiary-hover active:bg-button-normal-tertiary-active focus:shadow-border-focus focus:ring-default border-primary mr-2 w-4 rounded focus:outline-none focus:ring-4"
			ref={buttonRef}
			onClick={() =>
				expanded ? fieldExpander.collapse(prefix) : fieldExpander.expand(prefix)
			}
			data-testid="value-expand-button"
			onKeyDown={keydown}
		>
			{expanded ? (
				<ChevronDownIcon className="text-tertiary" size={16} />
			) : (
				<ChevronRightIcon className="text-tertiary" size={16} />
			)}
		</button>
	);

	// eslint-disable-next-line react-hooks/rules-of-hooks -- TODO
	const [copied, setCopied] = useState(false);
	// eslint-disable-next-line react-hooks/rules-of-hooks -- TODO
	const handleCopy = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			const value = prefix[prefix.length - 1]?.value;
			const text = typeof value === "string" ? value : safeStringify(value);
			try {
				await writeClipboardText(text);
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			} catch {
				// writeClipboardText rejects on failure — don't show the checkmark.
			}
		},
		[prefix]
	);

	const copyButton = (
		<button
			className={cx(
				classDoNotExpandobjectInspectorOnClick,
				"ml-2 shrink-0 rounded p-0.5",
				"text-tertiary hover:text-secondary",
				"opacity-0 transition-opacity group-hover/inspector-row:opacity-100",
				"hover:bg-button-normal-tertiary-hover active:bg-button-normal-tertiary-active",
				"focus:shadow-border-focus focus:ring-default focus:opacity-100 focus:outline-none focus:ring-4"
			)}
			onClick={handleCopy}
			data-testid="value-copy-button"
			title="Copy value"
		>
			{copied ? <CheckIcon className="text-green-600" size={14} /> : <CopyIcon size={14} />}
		</button>
	);

	// Disable a11y accessibility errors. We're making this UI accessible in other ways:
	//
	//   * Expand and collapse can be accomplished in the UI using the buttons on the lefthand side.
	//   * Expand/collapse button is navigable using both tab and the up/down arrows.
	//   * Clicking on the content expands/collapses the row, but also focuses the expand/collapse
	//     button for that row, providing both a visual indicator subsequently allowing key nav.
	const content = (
		<div
			onClick={(e) => {
				const shouldExpandOnClick =
					e.target instanceof Element &&
					!hasClassDoNotExpandObjectInspectorOnClick(e.target);
				if (expanded && shouldExpandOnClick) {
					fieldExpander.collapse(prefix);
				} else if (shouldExpandOnClick) {
					fieldExpander.expand(prefix);
				}
				navigator.focusCurrentButton();
			}}
			className={cx(
				showExpandButton ? "cursor-pointer" : "",
				// IMPORTANT: If the width is > 0, then the `whitespace-nowrap` in an ancestor
				// causes this to prevent dynamically shrinking the width of the row as a window
				// resizes.
				//
				// IF YOU DO CHANGE THIS: Check that window resizing works in the app and publish
				// templates. Make sure that code cells displaying the object expander grow and
				// shrink dynamically as window resizes, even when the expander is greater than the
				// row width.
				"w-0",
				"flex flex-row items-center"
			)}
		>
			{children}
			{showExpandButton && copyButton}
		</div>
	);

	let indentElement = <div />;
	if (showExpandButton) {
		indentElement = expandButton;
	} else if (indentLevel > 0) {
		indentElement = <div className="mr-2 w-4 shrink-0" />;
	}

	return (
		<div
			className={cx(
				classDoNotCollapseQueryBuilderOnClick,
				"group/inspector-row",
				`flex flex-row items-center font-mono`
			)}
			data-testid="inspector-row"
		>
			{spacing}
			{indentElement}
			{content}
		</div>
	);
};

export const InspectorRow = React.forwardRef(_InspectorRow);
